import { useParams, Link } from "react-router-dom";
import { useEffect, useReducer } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { ArrowLeft, Clock, Calendar, User, Tag, Share2 } from "lucide-react";
import { SeoHead } from "../../seo/SeoHead";
import { buildBreadcrumbListJsonLd } from "../../seo/site";
import { blogPosts } from "../../content/blog/posts";

// Helper pour charger le contenu markdown dynamiquement
const fetchMarkdown = async (slug) => {
  try {
    // Vite permet l'import dynamique. On utilise ?raw pour avoir le texte brut.
    const modules = import.meta.glob("../../content/blog/*.md", {
      query: "?raw",
      import: "default",
      eager: false,
    });
    const path = `../../content/blog/${slug}.md`;

    if (modules[path]) {
      return await modules[path]();
    }
    throw new Error("Article non trouvé");
  } catch (e) {
    console.error(e);
    return null;
  }
};

export default function BlogPostPage() {
  const { slug } = useParams();

  const blogReducer = (state, action) => {
    switch (action.type) {
      case "LOAD_START":
        return { content: "", loading: true };
      case "LOAD_DONE":
        return {
          content:
            action.content ||
            "# Article introuvable\nDésolé, cet article n'existe pas ou a été déplacé.",
          loading: false,
        };
      default:
        return state;
    }
  };
  const [{ content, loading }, dispatch] = useReducer(blogReducer, {
    content: "",
    loading: true,
  });

  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    if (slug) {
      dispatch({ type: "LOAD_START" });
      fetchMarkdown(slug).then((text) => {
        dispatch({ type: "LOAD_DONE", content: text });
      });
    }
  }, [slug]);

  if (!post && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-semibold mb-4">404</h1>
          <p className="mb-6 text-stone-600">Article introuvable</p>
          <Link to="/blog" className="text-[#A43032] font-bold hover:underline">
            Retour au blog
          </Link>
        </div>
      </div>
    );
  }

  const articleJsonLd = post
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: post.excerpt,
        image: post.image,
        datePublished: post.date,
        author: { "@type": "Person", name: post.author },
        publisher: { "@type": "Organization", name: "Raguia" },
      }
    : null;

  return (
    <>
      <SeoHead
        title={`${post?.title || "Chargement..."} — Blog Raguia`}
        description={post?.excerpt}
        jsonLd={[
          buildBreadcrumbListJsonLd([
            { name: "Accueil", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post?.title, path: `/blog/${slug}` },
          ]),
          ...(articleJsonLd ? [articleJsonLd] : []),
        ]}
      />

      <main className="min-h-screen bg-white pb-20">
        {/* HEADER ARTICLE */}
        <header className="bg-stone-50 border-b border-stone-200 pt-16 pb-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-bold text-stone-500 hover:text-[#A43032] mb-8 transition-colors"
            >
              <ArrowLeft className="size-4" /> Retour au blog
            </Link>

            <div className="flex flex-wrap gap-3 mb-6">
              {post?.tags?.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#A43032]/10 border border-[#A43032]/20 px-3 py-1 text-xs font-bold text-[#A43032]"
                >
                  <Tag className="size-3" />
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-[#010101] leading-tight mb-8">
              {post?.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-sm text-stone-500 font-medium">
              <div className="flex items-center gap-2">
                <User className="size-4 text-[#A43032]" />
                {post?.author}
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="size-4 text-[#A43032]" />
                {post?.date}
              </div>
              <div className="flex items-center gap-2">
                <Clock className="size-4 text-[#A43032]" />
                {post?.readTime}
              </div>
            </div>
          </div>
        </header>

        {/* CONTENU ARTICLE */}
        <article className="max-w-4xl mx-auto px-4 sm:px-6 pt-12">
          {loading ? (
            <div className="space-y-4 animate-pulse">
              <div className="h-4 bg-stone-200 rounded w-3/4"></div>
              <div className="h-4 bg-stone-200 rounded"></div>
              <div className="h-4 bg-stone-200 rounded w-5/6"></div>
              <div className="h-4 bg-stone-200 rounded w-1/2"></div>
            </div>
          ) : (
            <div className="prose prose-stone prose-lg max-w-none prose-headings:text-[#010101] prose-headings:font-black prose-a:text-[#A43032] prose-strong:text-[#010101] prose-blockquote:border-[#A43032] prose-blockquote:bg-stone-50 prose-blockquote:py-1 prose-blockquote:rounded-r-lg">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content}
              </ReactMarkdown>
            </div>
          )}

          {/* FOOTER ARTICLE */}
          <div className="mt-16 pt-10 border-t border-stone-200 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="size-12 rounded-full bg-[#A43032] flex items-center justify-center text-white font-bold">
                VF
              </div>
              <div>
                <p className="font-bold text-[#010101]">{post?.author}</p>
                <p className="text-xs text-stone-500 font-medium">
                  Expert IA chez Raguia
                </p>
              </div>
            </div>
            <Button variant="outline" className="gap-2">
              <Share2 className="size-4" /> Partager l'article
            </Button>
          </div>
        </article>

        {/* CTA FINAL */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 mt-20">
          <Card className="bg-[#010101] border-0 overflow-hidden">
            <CardContent className="p-8 md:p-12 text-center relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#A43032]/20 to-transparent" />
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4 relative z-10">
                Vous voulez tester le RAG sur vos documents ?
              </h2>
              <p className="text-stone-400 mb-8 max-w-xl mx-auto relative z-10">
                Mise en place en 24h, hébergement France, conforme RGPD.
              </p>
              <Button asChild variant="brand" size="lg">
                <Link to="/contact" className="inline-flex">
                  Essayer gratuitement
                </Link>
              </Button>
            </CardContent>
          </Card>
        </section>
      </main>
    </>
  );
}
